import dayjs, { ManipulateType } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import timezonePlugin from 'dayjs/plugin/timezone'
import { format } from './DateService'
import { TimeUnit } from './IDateService'

export class DateTime {
  private _time: number
  private timezone: string

  private get timeInServiceFormat () {
    return dayjs(this._time).tz(this.timezone)
  }

  get time () {
    return this._time
  }

  constructor (_date?: string | number | Date | bigint, timezone?: string) {
    const date = typeof _date === 'bigint' ? Number(_date) : _date

    this.timezone = timezone || 'Asia/Singapore'

    this.initService()

    if (!date) {
      this._time = dayjs().tz(this.timezone).valueOf()
      return
    }

    const res = dayjs(date).tz(this.timezone).valueOf()
    if (!res) throw new Error('Invalid Date')
    this._time = res
  }

  private initService () {
    dayjs.extend(customParseFormat)
    dayjs.extend(utc)
    dayjs.extend(timezonePlugin)
    dayjs.prototype.format = format
  }

  format (format: string): string {
    return this.timeInServiceFormat.format(format)
  }

  /**
   *
   * @returns Return day of week, (0) Sunday - (6) Saturday
   */
  day () {
    return dayjs(this._time).day()
  }

  formatUnixToDate (unix: number | bigint, format: string): string {
    const date = typeof unix === 'bigint' ? Number(unix) : unix
    return dayjs(date).format(format)
  }

  add (amount: number, timeUnit: TimeUnit) {
    this._time = this.timeInServiceFormat.add(amount, timeUnit as ManipulateType).valueOf()
    return this
  }

  subtract (amount: number, timeUnit: TimeUnit) {
    this._time = this.timeInServiceFormat.subtract(amount, timeUnit as ManipulateType).valueOf()
    return this
  }

  get (timeUnit: Exclude<TimeUnit, 'week'>) {
    return this.timeInServiceFormat.get(timeUnit)
  }

  set (value: number, timeUnit: Exclude<TimeUnit, 'week'>) {
    this._time = this.timeInServiceFormat
      .set(timeUnit, value)
      .valueOf()
    return this
  }

  startOf (timeUnit: Exclude<TimeUnit, 'week'>) {
    this._time = this.timeInServiceFormat.startOf(timeUnit).valueOf()
    return this
  }

  difference (date: DateTime, timeUnit: TimeUnit = 'millisecond', floatingValue = true) {
    return this.timeInServiceFormat.diff(date.timeInServiceFormat, timeUnit, floatingValue)
  }

  toDate (): Date {
    return this.timeInServiceFormat.toDate()
  }

  clone () {
    return new DateTime(this._time, this.timezone)
  }

  setTimezone (timezone: string) {
    this.timezone = timezone
    return this
  }

  getEndOfTheDay () {
    return this.timeInServiceFormat.utc().endOf('day').valueOf()
  }

  isBefore (toCompareTime: number) {
    return this.timeInServiceFormat.isBefore(toCompareTime)
  }

  isAfter (toCompareTime: number) {
    return this.timeInServiceFormat.isAfter(toCompareTime)
  }

  isSame (toCompareTime: number) {
    return this.timeInServiceFormat.isSame(toCompareTime)
  }
}
